/**
 * useCreateDocumentDatabase Hook
 *
 * Custom React Query mutation hook for creating document databases.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentDatabaseService } from '../../../api/document-database.service';
import type { DocumentDatabaseCreateDTO } from '../../../types/document-database.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateDocumentDatabaseOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating document database
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCreateDocumentDatabase = (tenantId: string, options?: UseCreateDocumentDatabaseOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DocumentDatabaseCreateDTO) => documentDatabaseService.createDocumentDatabase(tenantId, data),
    onSuccess: () => {
      // Invalidate all document database queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['documentDatabases', tenantId],
        exact: false
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
